import type { Language } from '@eformulieren/engine'
import type { Projection } from '@eformulieren/model'
import { answerOf, localeOf } from '@eformulieren/renderer'
import type * as Y from 'yjs'

/**
 * Wat de lezer ziet als hij geen toegang (meer) heeft tot dit formulier (audit delen van 30 oktober
 * 2026, R-66, R-155d).
 *
 * Twee aanleidingen, en één pagina. De toegang eindigt terwijl het formulier openstaat (de pod sluit
 * de verbinding met `geen-recht`, `access.ts`), of de pagina komt op en de pod zegt 404 op
 * `/projectie`: dan bestaat het formulier niet of niet voor deze persoon (9.2 regel 1). Eerder stond
 * er bij het eerste een rode regel "ververs de pagina" en bij het tweede het ruwe antwoord van de pod,
 * "GET /projectie gaf 404: {…}". Nu een pagina in gewone taal: wat er is gebeurd, door wie in
 * termen van een rol (de pod zegt niet wie, dus de zin noemt de twee mogelijkheden), en wat de lezer
 * kan doen. Stond er invoer die de pod nog niet had bevestigd, dan staat die eronder om te kopiëren.
 */
export type AccessEndedKind = 'beeindigd' | 'geen-toegang'

const TEXT = {
  beeindigd: {
    titel: { nl: 'U hebt geen toegang meer tot dit formulier', en: 'You no longer have access to this form' },
    uitleg: {
      nl: 'Uw toegang is {wanneer}ingetrokken of vervallen: degene die u uitnodigde heeft de deling ingetrokken, of NWO heeft uw rol in dit dossier gewijzigd. Wat u had ingevuld en al was opgeslagen, blijft bewaard in het formulier.',
      en: 'Your access was {wanneer}withdrawn or has lapsed: the person who invited you withdrew the share, or NWO changed your role in this case. What you entered and had already been saved is kept in the form.',
    },
  },
  'geen-toegang': {
    titel: { nl: 'U hebt geen toegang tot dit formulier', en: 'You do not have access to this form' },
    uitleg: {
      nl: 'Dit formulier bestaat niet, of u hebt er (nu) geen toegang toe. Dat kan zijn omdat een deling is ingetrokken of omdat uw rol in dit dossier is gewijzigd.',
      en: 'This form does not exist, or you do not have access to it (any more). A share may have been withdrawn, or your role in this case may have changed.',
    },
  },
  wat: {
    nl: 'Denkt u dat u nog toegang hoort te hebben? Vraag degene die u uitnodigde om een nieuwe uitnodiging, of neem contact op met NWO.',
    en: 'Do you think you should still have access? Ask the person who invited you for a new invitation, or contact NWO.',
  },
  /**
   * Bij "geen toegang" (een 404), en altijd daar: wie langs de andere ingang binnenkwam dan zijn toekenning
   * noemt, ziet het formulier niet (R-155a). De zin staat er ook als het formulier niet bestaat, zodat hij
   * niets verklapt (besluit van 3 november 2026).
   */
  andereKant: {
    nl: 'Bent u misschien aan de andere kant ingelogd? Wie als aanvrager is uitgenodigd, ziet het formulier niet als medewerker van NWO, en andersom. Log dan in aan de andere kant.',
    en: 'Are you perhaps signed in on the other side? Someone invited as an applicant does not see the form as an NWO employee, and the other way round. In that case, sign in on the other side.',
  },
  kopie: { nl: 'Wat u net typte en nog niet was opgeslagen', en: 'What you just typed and had not been saved yet' },
  kopieUitleg: {
    nl: 'Alleen op dit scherm, niet verstuurd. Kopieer het als u het wilt bewaren.',
    en: 'Only on this screen, not sent. Copy it if you want to keep it.',
  },
} as const

/**
 * De teksten van de pagina. `moment` is wanneer dit venster de sluiting kreeg: de pod zegt niet wie het
 * deed of wanneer precies, maar de sluiting komt binnen tien tot twintig seconden (ontwerp 6.4), dus het
 * moment van dit venster is eerlijk genoeg om te noemen.
 */
export function accessEndedText(
  kind: AccessEndedKind,
  language: Language,
  moment?: string,
): { titel: string; uitleg: string; wat: string; andereKant?: string; kopie: string; kopieUitleg: string } {
  const when = moment === undefined ? '' : `${onMoment(moment, language)} `
  return {
    titel: TEXT[kind].titel[language],
    uitleg: TEXT[kind].uitleg[language].replace('{wanneer}', when),
    wat: TEXT.wat[language],
    ...(kind === 'geen-toegang' ? { andereKant: TEXT.andereKant[language] } : {}),
    kopie: TEXT.kopie[language],
    kopieUitleg: TEXT.kopieUitleg[language],
  }
}

/** "op 30 oktober om 14:10". */
function onMoment(moment: string, language: Language): string {
  const date = new Date(moment)
  const day = new Intl.DateTimeFormat(localeOf(language), { day: 'numeric', month: 'long' }).format(date)
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  return language === 'nl' ? `op ${day} om ${time}` : `on ${day} at ${time}`
}

/**
 * De tekst van wat er in de documenten staat, als "label: antwoord" per regel, voor wie zijn invoer
 * wil bewaren. Alleen velden met tekst; een bijlage of een rij is geen tekst om over te nemen.
 */
export function copyableText(
  projection: Projection,
  documents: ReadonlyMap<string, Y.Doc>,
  language: Language,
  /** Alleen deze secties: die waar de toegang net veranderde (besluit 9). */
  only?: ReadonlySet<string>,
  /** Alleen deze velden: die na een generatienaad niet terug konden (eisenboek 12.50). */
  onlyFields?: ReadonlySet<string>,
): string {
  const lines: string[] = []
  for (const section of projection.secties) {
    const doc = documents.get(section.id)
    if (doc === undefined || section.recht !== 'bewerken' || (only !== undefined && !only.has(section.id))) continue
    for (const field of section.velden) {
      if (onlyFields !== undefined && !onlyFields.has(field.id)) continue
      const answer = answerOf({ veld: field, doc })
      if (answer === undefined || !('waarde' in answer)) continue
      const value = answer.waarde
      if (typeof value !== 'string' && typeof value !== 'number') continue
      const text = String(value).trim()
      if (text === '') continue
      const label = field.label === undefined ? field.id : field.label[language]
      lines.push(`${label}: ${text}`)
    }
  }
  return lines.join('\n\n')
}
